import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, XCircle, ArrowRight, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic payment result page. URL: /payments/:result?rsvp_id=...&type=vip&payment_id=...
 * Polls the backend RSVP status for up to ~10s when on success page (webhook may still be processing).
 */
const PaymentResult = ({ result }) => {
  const [searchParams] = useSearchParams();
  const rsvpId = searchParams.get('rsvp_id');
  const type = searchParams.get('type');
  const mpStatus = searchParams.get('status');
  const paymentId = searchParams.get('payment_id');

  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (result !== 'success' || !rsvpId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const resp = await axios.get(`${API}/payments/status/${rsvpId}`, {
          withCredentials: true,
        });
        setRsvpStatus(resp.data.payment_status);
        if (resp.data.payment_status === 'completed' || attempts >= maxAttempts) {
          clearInterval(interval);
          setPolling(false);
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPolling(false);
        }
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [result, rsvpId]);

  const config = {
    success: {
      icon: <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto" />,
      title: type === 'vip' ? '¡Bienvenido a Mi Tribu VIP!' : '¡Pago aprobado!',
      description: type === 'vip'
        ? 'Tu suscripción VIP fue activada. Disfrutá acceso anticipado, badge VIP y notificaciones premium por los próximos 30 días.'
        : 'Tu reserva está confirmada. Te enviamos el detalle por email y podés ver el QR en tu perfil.',
      testid: 'payment-success',
    },
    pending: {
      icon: <Clock className="w-20 h-20 text-yellow-500 mx-auto" />,
      title: 'Pago en proceso',
      description: 'Tu pago está siendo verificado. Vas a recibir un email cuando se confirme. Esto puede tardar unos minutos.',
      testid: 'payment-pending',
    },
    failure: {
      icon: <XCircle className="w-20 h-20 text-red-500 mx-auto" />,
      title: 'No pudimos procesar el pago',
      description: 'Tu pago fue rechazado o cancelado. Tu lugar quedó liberado. Intentá nuevamente o usá otro medio de pago.',
      testid: 'payment-failure',
    },
  }[result];

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFF7ED 100%)'}}>
      <Card className="w-full max-w-md bg-white border-[#E5E5E5] shadow-2xl" data-testid={config.testid}>
        <CardHeader className="text-center space-y-4">
          {config.icon}
          <CardTitle className="text-2xl font-heading font-bold text-[#0A0A0A]">
            {config.title}
          </CardTitle>
          <CardDescription className="text-[#525252] text-base">
            {config.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {result === 'success' && rsvpId && (
            <div className="bg-[#F5F5F5] rounded-lg p-3 text-xs text-[#525252] space-y-1">
              <div><strong className="text-[#0A0A0A]">Reserva:</strong> {rsvpId}</div>
              {paymentId && <div><strong className="text-[#0A0A0A]">Pago MercadoPago:</strong> {paymentId}</div>}
              {polling && <div className="text-yellow-600">Confirmando con MercadoPago...</div>}
              {!polling && rsvpStatus === 'completed' && <div className="text-green-600">✓ Reserva confirmada en sistema</div>}
              {!polling && rsvpStatus === 'pending' && <div className="text-yellow-600">⏳ Esperando confirmación del webhook (refrescá en unos segundos)</div>}
            </div>
          )}

          {mpStatus && result !== 'success' && (
            <div className="bg-[#F5F5F5] rounded-lg p-3 text-xs text-[#525252]">
              <strong className="text-[#0A0A0A]">Estado MercadoPago:</strong> {mpStatus}
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            {result === 'success' ? (
              <Link to="/perfil">
                <Button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="go-to-profile">
                  Ver mis reservas
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to="/explorar">
                <Button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="back-to-explore">
                  Seguir explorando
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
            <Link to="/">
              <Button variant="outline" className="w-full border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]" data-testid="go-home">
                <Home className="w-4 h-4 mr-2" />
                Ir al inicio
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentResult;
